import { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { formatAddressShort, formatTime, type Job } from '@fsm/shared';

/**
 * The day, on a map, in the browser.
 *
 * Coordinates come from the server — each address is placed once and kept —
 * so this draws pins and nothing else. It does not geocode, which is the whole
 * point: geocoding per view is what turns a map into a monthly bill.
 *
 * Tiles are OpenStreetMap's, which need no key and no account. Their usage
 * policy expects attribution, which is why the control below is not optional
 * decoration. At a hundred users this should move to a paid tile host or a
 * self-hosted one; that is a change to `TILES` and nothing else.
 *
 * Three things about Leaflet decide how this is written, and each of them is
 * a way it silently renders nothing:
 *
 *   1. The container needs a real height before `L.map` runs. A flex child
 *      with no explicit height measures zero, and a zero-height map draws
 *      nothing and reports no error. Hence the inline height and the guard.
 *   2. The map must be told when its container resizes, or it keeps the size
 *      it had at creation and tiles stop halfway across.
 *   3. Its default marker icons are loaded from paths relative to the CSS,
 *      which a bundler rewrites and Leaflet then cannot find. Rather than
 *      patch that up, every marker here is a `divIcon` — plain HTML, no image
 *      to lose, and it can carry the job's number.
 */

/** Austin, for when there is nothing to fit to. */
const FALLBACK: [number, number] = [30.2672, -97.7431];

const TILES = {
  url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  maxZoom: 19,
};

interface Placed {
  job: Job;
  lat: number;
  lng: number;
}

export function JobsMap({
  jobs,
  techs,
  onOpen,
}: {
  jobs: Job[];
  /**
   * For the pin colours: a map of unlabelled dots is a map of nothing.
   *
   * `colorHex` is nullable because a technician who has never been given a
   * colour has none — `pinHtml` falls back to the house green. Declaring it
   * non-null here was simply wrong, and it is what the office actually hands
   * over.
   */
  techs: Array<{ id: string; name: string; colorHex: string | null }>;
  onOpen: (job: Job) => void;
}) {
  const holder = useRef<HTMLDivElement | null>(null);
  const map = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);
  const [selected, setSelected] = useState<Job | null>(null);

  /*
   * `onOpen` in a ref rather than a dependency: a marker's click handler is
   * bound once when the pin is drawn, and rebuilding every pin because the
   * parent re-rendered would make clicking one a race against its own
   * redraw.
   */
  const open = useRef(onOpen);
  open.current = onOpen;

  const byTech = useMemo(() => new Map(techs.map((tech) => [tech.id, tech])), [techs]);

  const placed = useMemo<Placed[]>(
    () =>
      jobs.flatMap((job) => {
        const lat = job.property?.latitude;
        const lng = job.property?.longitude;
        return typeof lat === 'number' && typeof lng === 'number' ? [{ job, lat, lng }] : [];
      }),
    [jobs],
  );

  /** Jobs the office has an address for but nobody has placed yet. */
  const unplaced = useMemo(
    () =>
      jobs.filter(
        (job) =>
          job.property &&
          (typeof job.property.latitude !== 'number' ||
            typeof job.property.longitude !== 'number'),
      ),
    [jobs],
  );

  /* --- the map itself, created once --------------------------------------- */

  useEffect(() => {
    if (map.current || !holder.current) return;

    const instance = L.map(holder.current, {
      center: FALLBACK,
      zoom: 11,
      /*
       * Wheel zoom is on. The usual argument against it — a cursor crossing
       * the map while reading scrolls the page into a different zoom level —
       * does not apply here: this map fills the pane and is the thing being
       * read, not something passed over on the way somewhere else.
       */
      scrollWheelZoom: true,
      attributionControl: true,
    });

    L.tileLayer(TILES.url, {
      attribution: TILES.attribution,
      maxZoom: TILES.maxZoom,
    }).addTo(instance);

    layer.current = L.layerGroup().addTo(instance);
    map.current = instance;

    /*
     * Leaflet caches its container size at creation. A map created inside a
     * panel that is still laying out — or one whose panel later changes size —
     * keeps the old size and renders tiles across part of the area only.
     */
    const observer = new ResizeObserver(() => instance.invalidateSize());
    observer.observe(holder.current);

    return () => {
      observer.disconnect();
      instance.remove();
      map.current = null;
      layer.current = null;
    };
  }, []);

  /* --- the pins, redrawn whenever the day changes -------------------------- */

  useEffect(() => {
    const instance = map.current;
    const pins = layer.current;
    if (!instance || !pins) return;

    pins.clearLayers();
    setSelected(null);

    placed.forEach((entry, i) => {
      const finished = ['completed', 'invoiced', 'paid'].includes(entry.job.status);
      const tech = byTech.get(entry.job.assignedTechId ?? '');
      const marker = L.marker([entry.lat, entry.lng], {
        /* The browser tooltip. Leaflet escapes this; nothing else interpolates
           customer data into markup. */
        title: tech ? `${entry.job.title} — ${tech.name}` : entry.job.title,
        icon: L.divIcon({
          className: '',
          html: pinHtml(i + 1, tech?.colorHex ?? null, finished),
          iconSize: [28, 28],
          iconAnchor: [14, 14],
        }),
      });

      marker.on('click', () => setSelected(entry.job));
      marker.addTo(pins);
    });

    if (placed.length > 0) {
      instance.fitBounds(L.latLngBounds(placed.map((p) => [p.lat, p.lng] as [number, number])), {
        padding: [48, 48],
        maxZoom: 15,
      });
    }
  }, [placed, byTech]);

  return (
    <div className="relative min-h-0 flex-1">
      {/*
        An explicit height, not just flex-1. Leaflet measures this element the
        moment it is created, and a flex child mid-layout can measure zero —
        which draws an empty grey box and reports nothing wrong.
      */}
      <div ref={holder} className="absolute inset-0" style={{ height: '100%', width: '100%' }} />

      {placed.length === 0 && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="pointer-events-auto max-w-sm rounded-lg border border-line bg-surface p-4 text-center shadow-sm">
            <p className="text-[13.5px] font-semibold text-ink">Nothing to show on the map</p>
            <p className="mt-1 text-[13px] text-ink-3">
              {jobs.length === 0
                ? 'No jobs in this range.'
                : 'None of these addresses have been placed yet. They appear here once the server has looked them up.'}
            </p>
          </div>
        </div>
      )}

      {unplaced.length > 0 && placed.length > 0 && (
        <div className="absolute left-3 top-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-800 shadow-sm">
          {unplaced.length} not placed yet
        </div>
      )}

      {selected && (
        <div className="absolute bottom-3 left-3 w-72 rounded-lg border border-line bg-surface p-3 shadow-md">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[12.5px] font-medium text-ink-3">
              {selected.scheduledStart ? formatTime(selected.scheduledStart) : 'Unscheduled'}
            </span>
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="text-[12.5px] text-ink-4 hover:text-ink"
            >
              Close
            </button>
          </div>
          <p className="mt-1 truncate text-[13.5px] font-semibold text-ink">{selected.title}</p>
          <p className="truncate text-[12.5px] text-ink-3">
            {selected.customer?.name}
            {selected.property ? ` · ${formatAddressShort(selected.property)}` : ''}
          </p>
          <button
            type="button"
            onClick={() => open.current(selected)}
            className="mt-2 text-[13px] font-semibold text-accent hover:underline"
          >
            Open this job →
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * A pin, as an HTML string rather than a component.
 *
 * Leaflet's `divIcon` takes HTML, not React, so these markers live outside the
 * React tree entirely. Two consequences worth stating: the colour has to be an
 * inline style rather than a Tailwind class, because the technician's colour
 * comes from the database and Tailwind only ships classes it saw at build
 * time; and nothing here is interpolated from customer data. The only values
 * that reach this string are a number and a colour that has been checked
 * against a hex pattern.
 */
function pinHtml(index: number, colorHex: string | null, finished: boolean): string {
  const color = safeHex(colorHex) ?? '#225d4d';
  const style = finished
    ? `background:#fff;color:${color};border-color:${color}`
    : `background:${color};color:#fff;border-color:#fff`;

  return (
    `<span style="${style};display:flex;align-items:center;justify-content:center;` +
    `width:28px;height:28px;border-radius:9999px;border-width:2px;border-style:solid;` +
    `font-size:12px;font-weight:700;box-shadow:0 1px 3px rgba(0,0,0,.35)">${index}</span>`
  );
}

/**
 * A colour, or nothing.
 *
 * This value goes into a style attribute, so it is checked rather than
 * trusted. A technician's colour is set in the office app and could in
 * principle be anything the database holds; `#fff" onload="…` in a style
 * attribute is not an attack this app is otherwise exposed to, and it costs
 * one regex to keep it that way.
 */
function safeHex(value: string | null): string | null {
  if (!value) return null;
  return /^#[0-9a-fA-F]{3,8}$/.test(value) ? value : null;
}
